import type { Schemas } from '@suvoda-uat/contracts';
import { Icon } from './Icon';

/** The unresolved-count breakdown PrepareDownload returns when not ready (US3). */
export type OutstandingTestCases = Schemas['OutstandingTestCases'];

export type DownloadGateProps = {
  /**
   * The `outstanding` arm of PrepareDownload: how many Test Cases are still
   * incomplete / undecided, plus the dedup'd ids of every unresolved one
   * (FR-034). `testCaseIds.length` is the number still needing attention.
   */
  outstanding: OutstandingTestCases;
  /** Jump to the chosen unresolved Test Case (presentational — the consumer navigates). */
  onSelectTestCase?: (testCaseId: string) => void;
};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Not-ready surface for the FR-034 download gate. The Test Script DOCX can only
 * be downloaded once every Test Case is complete AND has a definitive Inclusion
 * Decision; until then PrepareDownload returns this breakdown. We show the
 * incomplete + undecided counts and a row per unresolved Test Case so the
 * designer can jump straight to it. Presentational: navigation is a callback.
 */
export function DownloadGate({ outstanding, onSelectTestCase }: DownloadGateProps) {
  const { incompleteCount, undecidedCount, testCaseIds } = outstanding;
  const total = testCaseIds.length;
  const summaryVerb = total === 1 ? 'needs' : 'need';

  return (
    <section
      aria-label="Download not ready"
      className="flex flex-col gap-3 rounded-md border border-[#f7e3a1] bg-[#fff7d6] px-4 py-3 text-md text-ink-800"
    >
      <p className="m-0 font-semibold">
        {plural(total, 'Test Case')} still {summaryVerb} attention before you can download.
      </p>
      <p className="m-0 text-sm text-text-secondary">
        {plural(incompleteCount, 'incomplete')} · {plural(undecidedCount, 'undecided')}
      </p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {testCaseIds.map((id) => (
          <li key={id}>
            <button
              type="button"
              aria-label={`Go to ${id}`}
              onClick={() => onSelectTestCase?.(id)}
              className="inline-flex w-full items-center justify-between gap-2 rounded-sm border-0 bg-transparent px-2 py-1 text-left text-md text-text-link hover:bg-ink-100"
            >
              <span>{id}</span>
              <Icon name="chevronRight" size={14} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
